import { useEffect, useId, useRef, type ReactNode } from "react";
import { Spinner } from "../Spinner";
import { buyerDataClassName } from "../../lib/buyerPrivacy";

/**
 * A question with a Cancel and a confirming button. On a phone it is a sheet at the foot of the screen.
 * `popover` hangs from the top right under a writing strip (the blog's post dialogs); `center` floats in the middle of the screen.
 */
export function ConfirmDialog({ title, children, confirmLabel, cancelLabel = "Cancel", alternative, tone = "action", placement = "center", buyerData = false, busy, onConfirm, onCancel, onAlternative }: {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** A quieter third choice on the left, such as "Schedule instead". */
  alternative?: string;
  tone?: "action" | "danger";
  placement?: "popover" | "center";
  buyerData?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onAlternative?: () => void;
}) {
  const dialog = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEffect(() => { dialog.current?.focus({ preventScroll: true }); }, []);
  return <>
    <div className="bw-area-dialog-scrim" data-placement={placement} aria-hidden="true" onClick={() => { if (!busy) onCancel(); }} />
    <div ref={dialog} className={buyerDataClassName("bw-area-dialog", buyerData)} data-placement={placement} role="dialog" aria-busy={busy} aria-labelledby={titleId} tabIndex={-1} onKeyDown={(event) => { if (event.key === "Escape") { event.stopPropagation(); if (!busy) onCancel(); } }}>
      <h2 id={titleId}>{title}</h2>
      {children}
      <div className="bw-area-dialog-actions">
        {onAlternative && alternative ? <button type="button" className="bw-btn bw-btn--text bw-area-dialog-alt" disabled={busy} onClick={onAlternative}>{alternative}</button> : <span className="bw-area-dialog-gap" />}
        <button type="button" className="bw-btn bw-btn--quiet" disabled={busy} onClick={onCancel}>{cancelLabel}</button>
        <button type="button" className={tone === "danger" ? "bw-btn bw-btn--danger" : "bw-btn bw-btn--primary bw-btn--action"} disabled={busy} aria-busy={busy} onClick={onConfirm}>{busy && <Spinner />}{confirmLabel}</button>
      </div>
    </div>
  </>;
}
